import { InvalidStateError } from '../errors/internal-error';
import { type TypeofOption, type TypeofOptionExec } from '../types/common';

/**
 * User defined type guard, which guarantees 'object is T', not undefined, not null
 * @see https://2ality.com/2020/06/type-guards-assertion-functions-typescript.html#user-defined-type-guards
 * @param {T | undefined | null} variable
 * @return {object is T}
 */
export const isDefined = <T>(variable: T | undefined | null): variable is NonNullable<T> => variable !== undefined && variable !== null;
export const isDefinedSome = (...variables: Array<any>): boolean => variables.some((variable) => isDefined(variable));
export const isDefinedAll = (...variables: Array<any>): boolean => variables.every((variable) => isDefined(variable));
export const assertIsDefined = <T>(variable: T | undefined | null, message?: string): asserts variable is NonNullable<T> => {
	if (!isDefined(variable)) throw new InvalidStateError(message);
};

/**
 * Type guard, which guarantees object is defined but empty
 * @param {'' | T | undefined | null} value
 * @return {value is '' | undefined | null}
 */
export const isEmpty = <T>(value: T | undefined | null | ''): value is undefined | null | '' => !isDefined(value) || value === '';

/**
 * Checks if the given input is an empty object
 * @param value Any value to check
 * @returns If the given input is an empty object
 */
export const isEmptyObject = <T extends object>(value: T | undefined | null): boolean => isObject(value) && Object.keys(value).length === 0;

/**
 * Type guard, which guarantees the input is a number and not NaN
 * @param {V | undefined | null | number} val
 * @return {val is number}
 */
export const isNumber = <V>(val: V | undefined | null | number): val is number => typeof val === 'number' && !Number.isNaN(val);

/**
 * Checks if the given value is a valid date
 * @param value Any value to check
 * @returns True if the given value is a valid date
 */
export const isDate = (value: any): value is Date => value instanceof Date && !Number.isNaN(value.getTime());

/**
 * Guards the given input as a valid date
 * @deprecated Use isDate above instead.
 */
export function isValidDate(value: unknown): value is Date {
	return value instanceof Date && !Number.isNaN(value) && Number(value) > 0;
}

/**
 * Checks if the given value is an object
 * @param value Any value to check
 * @returns True if the given value is an object
 */
export const isObject = (value: any): value is object => {
	const type = typeof value;
	return value !== null && (type === 'object' || type === 'function');
};

/**
 * Checks if the given value is an array
 * @param value Any value to check
 * @returns True if the given value is an array
 */
export const isArray = (value: any): value is any[] => Array.isArray(value);

/**
 * Checks for a Nil (null or undefined) value
 * @param value Any value to check
 * @returns True if the given value is null or undefined
 */
export const isNil = (value: any): value is null | undefined => value === null || value === undefined;

/**
 * Checks if the given value is a string
 * @param value Any value to check
 * @returns True if the given value is a string
 */
export const isString = (value: any): value is string => typeof value === 'string';

/**
 * Checks if the given value is a symbol
 * @param value Any value to check
 * @returns True if the given value is a symbol
 */
export const isSymbol = (value: any): value is symbol => typeof value === 'symbol';

/**
 * Generic type guard
 * @see https://rangle.io/blog/how-to-use-typescript-type-guards/
 * @param varToBeChecked
 * @param {(keyof T)[]} propertiesToCheckFor
 * @return {varToBeChecked is T}
 */
export const isOfType = <T>(varToBeChecked: any, propertiesToCheckFor?: (keyof T)[]): varToBeChecked is T => {
	if (isEmpty(propertiesToCheckFor)) return isDefined(varToBeChecked);
	return !propertiesToCheckFor.map((propToCheck) => (varToBeChecked as T)?.[propToCheck] !== undefined).includes(false);
};

/**
 * Checks whether the given value is of the specified type via typeof operator or instanceof.
 * @param value Unknown value to check
 * @param type Type to check against (e.g. 'string', 'boolean' or any constructor function, like Date or Error)
 * @returns Whether the value is of the specified type
 */
export function isTypeOf<TTypeOf extends TypeofOption>(value: unknown, type: TTypeOf): value is TypeofOptionExec<TTypeOf> {
	/** typeof null is 'object' */
	if (type === 'object' && value === null) return true;
	/** typeof array is 'object' */
	if (type === 'array') return isArray(value);
	/** instanceof check */
	if (typeof type === 'function') return value instanceof type;
	/** direct type check */
	if (typeof value === type) return true;
	return false;
}

/**
 * Array version of {@link isTypeOf} function.
 * @param value Unknown value to check
 * @param type Type to check against (e.g. 'string', 'boolean' or any constructor function, like Date or Error)
 * @returns Whether the value is an array of the specified type
 */
export function isArrayOf<TTypeOf extends TypeofOption>(value: unknown, type: TTypeOf): value is Array<TypeofOptionExec<TTypeOf>> {
	if (!isArray(value)) return false;
	return value.every((item) => isTypeOf<TTypeOf>(item, type));
}

/**
 * Returns if the given input is true (either boolean or string)
 * @param {boolean | string} value
 * @returns {boolean}
 */
export const isTrue = (value: boolean | string): boolean => value === true || value === 'true';

/**
 * Checks if the given value is a tuple of two (optional) dates
 * @param value Value to check
 * @returns True if the given value is a tuple of two (optional) dates
 */
export const isDateTuple = (value: any): value is [Date | null, Date | null] =>
	Array.isArray(value) && value.length === 2 && value.every((v) => v instanceof Date || v === null);

/**
 * Returns if the given input is a valid date tuple, meaning it is:
 * - an array of two items
 * - both items are instances of Date
 * - the first date is before or equal to the second date
 * @param value Value to check
 * @returns True if the given value is a valid date tuple
 */
export const isValidDateTuple = (value: any): value is [Date, Date] => {
	if (!isDateTuple(value)) return false;
	if (!isDate(value[0]) || !isDate(value[1])) return false;
	if (value[0] > value[1]) return false;
	return true;
};
